import React, { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import type { ContactListDto } from '@features/contacts';
import { useSalesforceViewModel } from '@features/contacts/hooks/useSalesforceViewModel.ts';
import { useTranslation } from 'react-i18next';
import { Input } from '@core-ui/Components/ui/input.tsx';
import { IconArrowLeft, IconFileFilled, IconFolder } from '@tabler/icons-react';
import { IntegrationsListsFallback } from '@features/contacts/components/IntegrationsListsFallback.tsx';
import { ContactListItemSkeleton } from '@features/contacts/components/ContactListSidebar.tsx';
import { ScrollArea } from '@core-ui/Components/ui/scroll-area.tsx';
import { SalesforceLogoTyped } from '@assets/index.ts';
import { Badge } from '@core-ui/Components/ui/badge.tsx';

function safeString (value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

interface SalesforceContactsProps {
  contactLists: ContactListDto[];
  onSelectContactList: (contactListId: string) => void;
  salesforceConnected: boolean;
}

export default function salesforceContacts ({ onSelectContactList, contactLists, salesforceConnected }: SalesforceContactsProps ): React.JSX.Element {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [folderSelected, setFolderSelected] = useState('');
  const [salesforceReports, setSalesforceReports] = useState<ContactListDto[]>([]);
  const [selectedContactListId, setSelectedContactListId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { retrieveSalesforceLists, retrieveSalesforceReports, salesforceSearch } = useSalesforceViewModel();

  const fetchSalesforceFolders = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const folders = await retrieveSalesforceLists();
    if (folders !== undefined) {
      setSalesforceReports(folders);
    }
    setIsLoading(false);
  }, [retrieveSalesforceLists, salesforceReports]);

  const handleSelectFolder = async (folderName: string): Promise<void> => {
    setIsLoading(true);
    onSelectContactList('');
    setFolderSelected(folderName);
    const reports = await retrieveSalesforceReports(folderName);

    if (reports !== undefined) {
      setSalesforceReports(reports);
    }
    setIsLoading(false);
  };

  const handleListSearch = async (query: string): Promise<void> => {
    setIsLoading(true);
    setSearchQuery(query);

    if (folderSelected !== '') {
      const lists = await retrieveSalesforceReports(folderSelected);
      const filteredData = (lists ?? []).filter((list) =>
        safeString(list.title).toLowerCase().includes(query));
      setSalesforceReports(filteredData);
    } else {
      const lists = await salesforceSearch(query);
      setSalesforceReports(lists);
    }
    setIsLoading(false);
  };

  const handleSelectContactList = (contactList: ContactListDto): void => {
    if (contactList.id !== '') {
      onSelectContactList(contactList.id);
      setSelectedContactListId(contactList.id);
    }
  };

  const handleBackFolder = (): void => {
    setFolderSelected('');
    setSearchQuery('');
    void fetchSalesforceFolders();
    onSelectContactList('');
  };

  useEffect(() => {
    void fetchSalesforceFolders();
  }, []);

  return (
    <div>
      {folderSelected !== '' &&
        <div className="p-4 m-2 rounded-md bg-blue-250 border border-blue-200">
          <div className="flex items-center gap-2 text-blue-400">
            <button
              type="button"
              onClick={handleBackFolder}
              className="flex items-center justify-center rounded-md p-1 hover:bg-blue-200 transition-colors"
            >
              <IconArrowLeft className="h-4 w-4"/>
            </button>
            <IconFolder stroke={0.5} className="h-4 w-4"/>
            <span className="font-medium">
              Folder name: {folderSelected}
            </span>
          </div>
        </div>
      }

      <div className="p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
          <Input
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void handleListSearch(String(searchQuery));
              }
            }}
            className="pl-9"
            placeholder={(folderSelected !== '') ? t('contacts.sidebar.searchPh') : t('wizard.selectContacts.searchFolderPh')}
          />
        </div>
      </div>

      <ScrollArea className="h-[520px] md:h-[620px]">
        <div className="px-2 pb-2">
          { isLoading ? (
            <ContactListItemSkeleton rows={8} />
          ) : contactLists.length === 0 ? (
            !salesforceConnected ? (
              <IntegrationsListsFallback
                icon={SalesforceLogoTyped}
                message={t('contacts.table.noAccounts')}
                buttonText={t('contacts.table.gotoIntegration', { integration: 'SALESFORCE' })}
              />
            ) : (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                {t('contacts.sidebar.empty')}
              </div>
            )
          ) : (
            salesforceReports.map((contactList, index) => {
              const isSelected = contactList.id === selectedContactListId;
              const contactListTitle = safeString(contactList.title) !== ''
                ? safeString(contactList.title)
                : 'Untitled list';

              return (
                <div
                  key={index}
                  className={`group w-full px-3 py-3 transition border-b border-border/60 ${isSelected ? 'bg-muted/60' : 'hover:bg-muted/40'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {/* Contact List Button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (contactList.isFolder === true ) {
                          void handleSelectFolder(contactList.title);
                        } else {
                          handleSelectContactList(contactList);
                        }
                      }}
                      className="min-w-0 flex-1 text-left"
                      title={contactListTitle}
                    >
                      <div className="flex items-center gap-2 line-clamp-2 break-words text-sm font-medium text-foreground">
                        {(contactList.isFolder === true) ?
                          (
                            <IconFolder stroke={0.5} className="mr-1 h-4 w-4" />
                          ) : (
                            <IconFileFilled stroke={0.5} className="mr-1 h-4 w-4" />
                          )
                        }
                        <span>{contactListTitle}</span>
                      </div>
                      <div>
                        <Badge className="mt-1 text-xs border-1 rounded-md bg-[var(--salesforce-badge-color)] border-[var(--salesforce-badge-border)] text-[var(--salesforce-badge-text)]">
                          {safeString('SALESFORCE')}
                        </Badge>
                      </div>
                    </button>
                  </div>
                </div>
              );
            }))}
        </div>
      </ScrollArea>


    </div>
  );
}